import React, {createRef, useState} from 'react';
import {Trans, useTranslation} from "react-i18next";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import {CardContent, CircularProgress, Modal, Paper, Slider, SliderValueLabel} from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import {ClearOutlined, DrawOutlined, FileUploadOutlined} from "@mui/icons-material";
import PhotoEditor from "../../photoEditor/PhotoEditor";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import FunctionHandler from "../../utils/functionHandler";
import functionHandler from "../../utils/functionHandler";
import CartManager, {useCartChange} from "../../managers/cartManager/cartManager";
import ImageLoader from "../../components/imageLoader/ImageLoader";

const fileTypes = ["JPG", "JPEG", "PNG", "SVG", "WEBP"];

function FileParam({id, values, onChange, value, forCart, inCart, props}) {
    const {t} = useTranslation();
    const cart = useCartChange();
    const {path, width, ratio} = value || {width: 15};

    const [opened, setOpened] = useState(false)
    const [editorOpen, setEditorOpen] = useState(false)
    const [selected, setSelection] = useState(path)
    const inputRef = createRef();
    const handleClick = React.useCallback(() => inputRef.current?.click(), [inputRef]);
    const [loading, setLoading] = useState(false);

    const onFileUpload = (event) => {
        const formData = new FormData();
        Array.from(event.target.files).forEach(file => {
                formData.append('file', file);
            }
        );
        setLoading(true);

        FunctionHandler.sendFile('/upload', formData, (data) => {
            CartManager.addDesignsPath(data.uploadedFiles);
            if (data.uploadedFiles.length) {
                setSelection(encodeURIComponent(data.uploadedFiles[0]));
            }

            setLoading(false);
        });

        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.type = "text";
            inputRef.current.type = "file";
        }
    }

    const handleCloseModal = (event) => {
        setOpened(false);
    }

    const closeEditor = (svg) => {
        setEditorOpen(false);
        if (svg) {
            setLoading(true);
            FunctionHandler.sendData('/uploadSvg', {svg}, (data, err) => {
                if (!err) {
                    CartManager.addDesignPath(data.filePath);
                    setSelection(encodeURIComponent(data.filePath));
                }
                setLoading(false);
            });
        }
    }

    const handleOpenEditor = (event) => {
        setEditorOpen(true);
    }

    const getDesigns = () => {
        return cart.designs.map((src, index) => {
            return <Grid key={src}
                         sx={{
                             padding: '2px',
                             border: '2px solid',
                             borderColor: selected === src ? 'secondary.main' : 'transparent'
                         }}>
                <Paper>
                    <ImageLoader
                        src={FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent('thumbnail/')}${src.replace(/.svg$/, '.png')}`)}
                        fallbackSrc={FunctionHandler.getFunctionUrl(`/dep/${src}`)}
                        sx={{
                            width: 50,
                            height: 50,
                            cursor: 'pointer'
                        }}
                        onClick={() => selectDesign(src)}/>
                </Paper>
            </Grid>
        });
    }

    const selectDesign = (src) => {
        if (selected === src) {
            setSelection('');
        } else {
            setSelection(src);
        }
    }

    const submitSelection = () => {
        functionHandler.getData(`/getMetadata/${selected}`, data => {
            console.log(data)
            const newRatio = data.height / data.width
            onChange('file', {path: selected, width: width, priceModifier: true, ratio: newRatio});
            handleCloseModal();
        })
    }

    const handleChange = (event, newWidth) => {
        onChange('file', {path: selected, width: newWidth, priceModifier: true, ratio});
    }

    const openModal = () => {
        setOpened(true)
    }

    const getLength = () => {
        const length = (ratio * width).toFixed(1);
        return `x ${length} cm `;
    }

    return (forCart ?
            <Typography sx={{color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', textWrap: 'nowrap'}}
                        variant={'body2'}>
                <Trans i18nKey={`params.size`}/>: {width.toFixed(1)} cm {ratio && getLength()}
            </Typography> :
            <FormControl fullWidth>
                <Typography id={`${id}-label`}>
                    <Trans i18nKey={`params.size`}/>:
                    <br/>{width.toFixed(1)} cm {ratio ? getLength() : t('params.width')}
                </Typography>
                <Slider
                    aria-label="slider"
                    value={width}
                    shiftStep={5}
                    step={1}
                    min={5}
                    max={28}
                    slots={{valueLabel: SliderValueLabel}}
                    onChange={handleChange}
                    color={'secondary'}
                />
                <Grid key={path} sx={{
                    padding: '2px',
                    border: '2px solid',
                    borderColor: 'transparent',
                    display: 'flex',
                    justifyContent: {
                        xs: 'center',
                        md: 'end'
                    },
                }}>
                    {path ? <ImageLoader
                        src={FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent('thumbnail/')}${path.replace(/.svg$/, '.png')}`)}
                        fallbackSrc={FunctionHandler.getFunctionUrl(`/dep/${path}`)}
                        sx={{
                            width: 50,
                            height: 50,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: 'primary.main',
                        }}
                        onClick={openModal}/> : <Button onClick={openModal}>Upload</Button>}

                </Grid>
                {loading ? <Box sx={{
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    backgroundColor: 'rgba(0, 0, 0, 0.35)'
                }}><CircularProgress/></Box> : ''}
                <Modal
                    open={opened}
                    onClose={handleCloseModal}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                >
                    <Paper
                        sx={{width: {xs: '100%', sm: 500}, maxHeight: '100%', overflowY: 'auto', overflowX: 'hidden'}}>
                        {editorOpen ? '' : <IconButton onClick={handleCloseModal}
                                                       sx={{position: 'absolute', top: -5, right: -5, zIndex: 100}}>
                            <ClearOutlined/>
                        </IconButton>}
                        {editorOpen ? <CardContent sx={{
                                height: {xs: '100%', sm: 'initial'},
                                p: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden'
                            }}>
                                <PhotoEditor closeEditor={closeEditor} fileTypes={fileTypes}/>
                            </CardContent> :
                            <CardContent sx={{height: {xs: '100%', sm: 'initial'}, pt: 3}}>
                                <Typography variant="body2" sx={{pb: 1}}>
                                    <Trans i18nKey={'design.warning'}/>
                                </Typography>
                                <Grid
                                    container
                                    flexDirection="column"
                                    justifyContent="space-between"
                                    sx={{minHeight: 450, height: {xs: '100%'}}}
                                >
                                    <Grid container gap={2}>
                                        <Grid sx={{
                                            padding: '2px',
                                            border: '2px solid',
                                            borderColor: 'transparent'
                                        }}>
                                            <CardContent sx={{
                                                width: 50,
                                                height: 50,
                                                cursor: 'pointer',
                                                border: '2px dashed',
                                                borderColor: 'primary.main'
                                            }} onClick={handleOpenEditor}>
                                                <DrawOutlined sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)'
                                                }} color="secondary"/>
                                            </CardContent>
                                        </Grid>
                                        <Grid sx={{
                                            padding: '2px',
                                            border: '2px solid',
                                            borderColor: 'transparent'
                                        }}>
                                            <CardContent sx={{
                                                width: 50,
                                                height: 50,
                                                cursor: 'pointer',
                                                border: '2px dashed',
                                                borderColor: 'primary.main'
                                            }} onClick={handleClick}>
                                                <FileUploadOutlined sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)'
                                                }} color="secondary"/>
                                                <input type="file" style={{display: 'none'}} ref={inputRef}
                                                       onChange={onFileUpload}
                                                       accept={fileTypes.map(ext => `image/${ext}`).join(', ')}
                                                       multiple={true}/>
                                            </CardContent>
                                        </Grid>
                                        {getDesigns()}
                                    </Grid>
                                    <Box sx={{justifyContent: 'flex-end', display: 'flex', pt: 3}}>
                                        <Button onClick={submitSelection} variant={"contained"} sx={{ml: 2}}>
                                            <Trans i18nKey={'buttons.submit'}/>
                                        </Button>
                                    </Box>
                                </Grid>
                            </CardContent>
                        }

                    </Paper>
                </Modal>
            </FormControl>
    );
}

export default FileParam;
